'use client'

import { setUser } from '@sentry/nextjs'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useRouter } from 'next/navigation'
import type { ReactNode } from 'react'
import { createContext, useCallback, useContext, useEffect } from 'react'
import { logout } from '@/actions/auth/logout'
import { getProfileAction } from '@/actions/user/get-profile-action'
import { useAuthStatus } from '@/contexts/auth-context'
import { resolveSessionUser } from '@/helpers/resolve-session-user'
import { unwrapProfile } from '@/helpers/unwrap-profile'
import type { Profile } from '@/http/zamply/user/get-profile-schema'

const SESSION_QUERY_KEY = ['session', 'me'] as const

const MS_PER_SECOND = 1000
const SECONDS_PER_MINUTE = 60
const SESSION_STALE_MINUTES = 5
const SESSION_GC_MINUTES = 30
const SESSION_STALE_MS =
  MS_PER_SECOND * SECONDS_PER_MINUTE * SESSION_STALE_MINUTES
const SESSION_GC_MS = MS_PER_SECOND * SECONDS_PER_MINUTE * SESSION_GC_MINUTES

async function fetchMe(): Promise<Profile> {
  const result = await getProfileAction()
  const profile = unwrapProfile(result.data)
  if (!profile) {
    throw new Error('Failed to fetch session')
  }
  return profile
}

type SessionContextValue = {
  isLoaded: boolean
  isSignedIn: boolean
  user: Profile | null
  refresh: () => Promise<void>
  signOut: () => Promise<void>
}

const SessionContext = createContext<SessionContextValue | null>(null)

export function SessionProvider({
  children,
  initialUser = null,
}: {
  children: ReactNode
  initialUser?: Profile | null
}) {
  const router = useRouter()
  const queryClient = useQueryClient()
  const authStatus = useAuthStatus()
  const isAuthenticated = authStatus === 'authenticated'
  const hasInitialUser = initialUser != null
  const sessionUserId = initialUser?.reference ?? 'anonymous'

  const { data, isPending } = useQuery({
    queryKey: [...SESSION_QUERY_KEY, sessionUserId],
    queryFn: fetchMe,
    enabled: isAuthenticated || hasInitialUser,
    initialData: initialUser ?? undefined,
    staleTime: SESSION_STALE_MS,
    gcTime: SESSION_GC_MS,
    refetchOnWindowFocus: false,
  })

  const refresh = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: SESSION_QUERY_KEY })
  }, [queryClient])

  const signOut = useCallback(async () => {
    await logout()
    queryClient.removeQueries({ queryKey: SESSION_QUERY_KEY })
    queryClient.removeQueries({ queryKey: ['atalia'] })
    router.push('/conta/login')
  }, [queryClient, router])

  const user = resolveSessionUser(data, initialUser)
  const userReference = user?.reference ?? null

  // Associa erros e logs ao usuário logado só pelo id (sem e-mail/CPF)
  useEffect(() => {
    setUser(userReference ? { id: userReference } : null)
  }, [userReference])

  return (
    <SessionContext.Provider
      value={{
        isLoaded:
          hasInitialUser ||
          (authStatus !== 'loading' && !(isAuthenticated && isPending)),
        isSignedIn: user != null,
        user,
        refresh,
        signOut,
      }}
    >
      {children}
    </SessionContext.Provider>
  )
}

function useSessionCtx() {
  const ctx = useContext(SessionContext)
  if (!ctx) {
    throw new Error('useSession/useUser must be used inside <SessionProvider>')
  }
  return ctx
}

export function useSession() {
  return useSessionCtx()
}

export function useUser() {
  const { isLoaded, isSignedIn, user } = useSessionCtx()
  return { isLoaded, isSignedIn, user }
}
